import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, CaretRight, Play } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = 'https://images.unsplash.com/photo-1542204165-65bf26472b9b?w=1920&q=80';

const ThemeBollywood = ({ user }) => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/movies`).then((r) => setMovies(r.data || [])).catch(() => {});
  }, []);

  const dashRoute = () => {
    if (user?.role === 'admin') return '/admin-dashboard';
    if (user?.role === 'producer') return '/producer-dashboard';
    if (user?.role === 'fan') return '/fan-dashboard';
    return '/investor-dashboard';
  };

  return (
    <div
      className="min-h-screen text-[#fff8dc]"
      style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        background:
          "linear-gradient(135deg, rgba(40,8,4,0.92) 0%, rgba(80,15,15,0.85) 40%, rgba(20,5,5,0.95) 100%)",
      }}
    >
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})`, filter: 'saturate(1.2) contrast(1.05)' }}
      />
      <div
        className="fixed inset-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse at 30% 40%, rgba(200,16,46,0.35) 0%, transparent 60%), radial-gradient(ellipse at 70% 70%, rgba(255,215,0,0.25) 0%, transparent 55%), linear-gradient(180deg, rgba(8,2,2,0.85) 0%, rgba(20,5,5,0.95) 100%)',
        }}
      />

      <nav data-web-chrome="nav" className="relative z-10 px-6 py-5 max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#FFD700]/40" data-testid="brand-logo" />
          <span
            className="text-3xl font-bold tracking-wider"
            style={{ fontFamily: "'Cinzel', serif", color: '#FFD700' }}
          >
            FanZingy
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="theme-bolly-marketplace"
            onClick={() => navigate('/marketplace')}
            className="px-5 py-2 border-2 border-[#FFD700]/40 hover:border-[#FFD700] text-[#FFD700] rounded-full text-sm font-bold tracking-wide transition-all"
          >
            Marketplace
          </button>
          {user ? (
            <button
              onClick={() => navigate(dashRoute())}
              className="px-6 py-2 bg-gradient-to-r from-[#C8102E] to-[#FFD700] text-black rounded-full text-sm font-extrabold tracking-wider hover:scale-105 transition-transform"
            >
              Dashboard
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-gradient-to-r from-[#C8102E] to-[#FFD700] text-black rounded-full text-sm font-extrabold tracking-wider hover:scale-105 transition-transform"
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      <section className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-20 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-sm tracking-[0.4em] text-[#FFD700] mb-6 uppercase">A Cinematic Investment</p>
          <h1
            style={{ fontFamily: "'Cinzel', serif" }}
            className="text-6xl md:text-8xl font-extrabold mb-8 leading-none"
          >
            <span style={{ color: '#FFD700', textShadow: '0 4px 30px rgba(255,215,0,0.4)' }}>
              Bollywood
            </span>
            <br />
            <span className="text-[#fff8dc] italic font-medium">meets profits.</span>
          </h1>
          <p className="text-xl md:text-2xl text-[#fff8dc]/80 mb-12 max-w-2xl mx-auto font-light leading-relaxed">
            Own a piece of India's biggest blockbusters. Earn royalties on every ticket sold, every stream watched.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="group px-10 py-5 bg-gradient-to-r from-[#C8102E] via-[#FF6B35] to-[#FFD700] text-black rounded-full text-lg font-bold tracking-wide shadow-2xl shadow-[#C8102E]/40 hover:shadow-[#FFD700]/40 transition-all flex items-center gap-3"
            >
              Start Investing
              <CaretRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/marketplace')}
              className="px-10 py-5 bg-[#fff8dc]/10 backdrop-blur-sm border border-[#FFD700]/30 text-[#fff8dc] rounded-full text-lg font-medium hover:bg-[#fff8dc]/15 transition-all flex items-center gap-3"
            >
              <Play size={18} weight="fill" /> Watch Trailer
            </button>
          </div>
        </motion.div>
      </section>

      {movies.length > 0 && (
        <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
          <h2
            style={{ fontFamily: "'Cinzel', serif" }}
            className="text-4xl font-bold mb-10 text-center"
          >
            <span className="text-[#FFD700]">NOW</span> <span className="text-[#fff8dc]">FUNDING</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {movies.slice(0, 4).map((m) => (
              <motion.button
                key={m.id}
                whileHover={{ y: -8 }}
                onClick={() => navigate(`/movie/${m.id}`)}
                className="relative aspect-[2/3] rounded-xl overflow-hidden border-2 border-[#FFD700]/20 hover:border-[#FFD700] transition-all group"
              >
                <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-[#FFD700] font-bold mb-1">{m.industry}</div>
                  <div style={{ fontFamily: "'Cinzel', serif" }} className="text-lg font-bold text-[#fff8dc]">{m.title}</div>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      <footer className="relative z-10 border-t border-[#FFD700]/10 py-8 text-center">
        <p style={{ fontFamily: "'Cinzel', serif" }} className="text-sm text-[#FFD700]/60 tracking-widest">
          © {new Date().getFullYear()} FANZINGY · BOLLYWOOD ROYALE THEME
        </p>
      </footer>
    </div>
  );
};

export default ThemeBollywood;
